import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const PAGE_SIZE_KEY = 'admin_wire_transfer_pageSize';

const WireTransferList = () => {
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSearchTerm, setTempSearchTerm] = useState('');
    const [selectedTransfer, setSelectedTransfer] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [approveStartDate, setApproveStartDate] = useState('');
    const [approveEndDate, setApproveEndDate] = useState('');
    const [approveMemo, setApproveMemo] = useState('');
    const [approveAmount, setApproveAmount] = useState('');
    const [approveGrade, setApproveGrade] = useState('premium');
    const [gradePrices, setGradePrices] = useState({ basic: 30000, premium: 50000 });
    const [statusFilter, setStatusFilter] = useState('');
    const [limit, setLimit] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_KEY);
        return saved ? parseInt(saved, 10) : 50;
    });

    const getVatAmount = (gradeName) => {
        const base = gradePrices[gradeName] || 0;
        return String(base + Math.round(base * 0.1));
    };

    const handleSetDays = (days) => {
        if (!approveStartDate) return;
        const start = new Date(approveStartDate);
        const end = new Date(start);
        end.setDate(end.getDate() + days);
        setApproveEndDate(end.toISOString().split('T')[0]);
    };

    const fetchTransfers = async (page = currentPage, search = searchTerm, lim = limit, status = statusFilter) => {
        setLoading(true);
        try {
            const res = await api.get('/settlement/wire-transfers', {
                params: { page, limit: lim, search, status }
            });
            if (res.data.success) {
                setTransfers(res.data.data);
                setTotalPages(Math.ceil(res.data.total / lim));
            }
        } catch (error) {
            console.error('무통장 입금 내역 조회 에러:', error);
            toast.error('무통장 입금 내역을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        if (!approveStartDate || !approveEndDate) {
            toast.error('시작일과 종료일을 모두 설정해주세요.');
            return;
        }
        if (approveStartDate > approveEndDate) {
            toast.error('종료일은 시작일보다 빠를 수 없습니다.');
            return;
        }
        if (!window.confirm("설정된 기간으로 구독을 승인하시겠습니까? 승인 시 사용자 세션이 갱신(강제 로그아웃)됩니다.")) return;
        
        try {
            const res = await api.post(`/settlement/wire-transfers/${id}/approve`, {
                startDate: approveStartDate,
                endDate: approveEndDate,
                memo: approveMemo,
                amount: approveAmount ? Number(approveAmount) : null,
                grade: approveGrade
            });
            if (res.data.success) {
                toast.success('입금 확인 및 승인이 완료되었습니다.');
                setShowDetailModal(false);
                fetchTransfers(currentPage, searchTerm);
            } else {
                toast.error(res.data.message || '승인 처리에 실패했습니다.');
            }
        } catch (err) {
            console.error('Approve error:', err);
            toast.error(err.response?.data?.message || '승인 처리 중 오류가 발생했습니다.');
        }
    };

    const handleUpdateDates = async (id) => {
        if (!approveStartDate || !approveEndDate) {
            toast.error('시작일과 종료일을 모두 설정해주세요.');
            return;
        }
        if (approveStartDate > approveEndDate) {
            toast.error('종료일은 시작일보다 빠를 수 없습니다.');
            return;
        }
        if (!window.confirm("설정된 기간으로 수정하시겠습니까? 수정 시 사용자 세션이 갱신(강제 로그아웃)됩니다.")) return;
        
        try {
            const res = await api.put(`/settlement/wire-transfers/${id}/dates`, {
                startDate: approveStartDate,
                endDate: approveEndDate,
                memo: approveMemo,
                grade: approveGrade,
                amount: approveAmount ? Number(approveAmount) : null
            });
            if (res.data.success) {
                toast.success('기간이 성공적으로 수정되었습니다.');
                setShowDetailModal(false);
                fetchTransfers(currentPage, searchTerm);
            } else {
                toast.error(res.data.message || '기간 수정에 실패했습니다.');
            }
        } catch (err) {
            console.error('Update dates error:', err);
            toast.error(err.response?.data?.message || '기간 수정 중 오류가 발생했습니다.');
        }
    };

    const handleReject = async (id) => {
        if (!window.confirm("이 신청건을 거절(반려) 처리하시겠습니까? 신청자에게 다시 신청할 수 있는 권한이 부여됩니다.")) return;
        
        try {
            const res = await api.post(`/settlement/wire-transfers/${id}/reject`, {
                memo: approveMemo
            });
            if (res.data.success) {
                toast.success('신청이 거절(반려) 처리되었습니다.');
                setShowDetailModal(false);
                fetchTransfers(currentPage, searchTerm);
            } else {
                toast.error(res.data.message || '거절 처리에 실패했습니다.');
            }
        } catch (err) {
            console.error('Reject error:', err);
            toast.error(err.response?.data?.message || '거절 처리 중 오류가 발생했습니다.');
        }
    };

    useEffect(() => {
        fetchTransfers(currentPage, searchTerm, limit, statusFilter);
    }, [currentPage, searchTerm, statusFilter]);

    useEffect(() => {
        api.get('/settings/grades-info').then(res => {
            if (res.data?.success && res.data.data) {
                const prices = {};
                res.data.data.forEach(g => { prices[g.grade_name] = Number(g.price) || 0; });
                setGradePrices(prices);
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (showDetailModal && selectedTransfer) {
            if (selectedTransfer.status === 'WIRE_TRANSFER_PENDING') {
                const today = new Date();
                const oneMonthLater = new Date();
                oneMonthLater.setMonth(today.getMonth() + 1);
                
                // 날짜 포맷 (YYYY-MM-DD) 추출 시 로컬 타임존 반영
                const tzOffset = today.getTimezoneOffset() * 60000;
                const localToday = new Date(today.getTime() - tzOffset);
                const localOneMonthLater = new Date(oneMonthLater.getTime() - tzOffset);
                
                setApproveStartDate(localToday.toISOString().split('T')[0]);
                setApproveEndDate(localOneMonthLater.toISOString().split('T')[0]);
            } else if (selectedTransfer.status === 'DONE') {
                let parsedInfo = {};
                try {
                    parsedInfo = typeof selectedTransfer.tax_invoice_info === 'string' ? JSON.parse(selectedTransfer.tax_invoice_info) : (selectedTransfer.tax_invoice_info || {});
                } catch(e){
                    console.error("Failed to parse tax_invoice_info:", e);
                }
                
                if (parsedInfo.approvedStartDate) {
                    setApproveStartDate(parsedInfo.approvedStartDate.split('T')[0]);
                }
                if (parsedInfo.approvedEndDate) {
                    setApproveEndDate(parsedInfo.approvedEndDate.split('T')[0]);
                }
            }
            setApproveMemo('');
            const grade = selectedTransfer.order_id?.startsWith('WIRE_basic_') ? 'basic' : 'premium';
            setApproveGrade(grade);
            // 고객 신청 금액이 있으면 그대로, 없으면 DB 등급 가격 + VAT
            setApproveAmount(selectedTransfer.amount ? String(selectedTransfer.amount) : getVatAmount(grade));
        }
    }, [showDetailModal, selectedTransfer]);

    const handleSearch = () => {
        setSearchTerm(tempSearchTerm);
        setCurrentPage(1);
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const renderPagination = () => {
        const pageNumbers = [];
        let startPage = Math.max(1, currentPage - 5);
        let endPage = Math.min(totalPages, startPage + 9);

        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <nav>
                <ul className="pagination pagination-sm justify-content-center mt-3">
                    {pageNumbers.map(number => (
                        <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(number)}>{number}</button>
                        </li>
                    ))}
                </ul>
            </nav>
        );
    };

    return (
        <>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-3">
                <div className="d-flex flex-wrap align-items-center gap-1">
                    <select
                        className="form-select form-select-sm"
                        value={statusFilter}
                        onChange={handleStatusFilter}
                        style={{ width: '130px' }}
                    >
                        <option value="">모든 상태</option>
                        <option value="WIRE_TRANSFER_PENDING">승인대기</option>
                        <option value="DONE">승인완료</option>
                        <option value="REJECTED">거절/취소</option>
                    </select>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="이름, 아이디, 입금자명, 상호 등 검색"
                        value={tempSearchTerm}
                        onChange={(e) => setTempSearchTerm(e.target.value)}
                        onKeyPress={handleKeyDown}
                        style={{ width: '240px' }}
                    />
                    <button className="btn btn-primary btn-sm" type="button" onClick={handleSearch}>
                        <i className="fas fa-search"></i> 검색
                    </button>
                    <button className="btn btn-secondary btn-sm" type="button" onClick={() => {
                        setTempSearchTerm('');
                        setSearchTerm('');
                        setStatusFilter('');
                        setCurrentPage(1);
                        fetchTransfers(1, '', limit, '');
                    }}>
                        <i className="fas fa-sync-alt"></i> 초기화
                    </button>
                </div>
                <div className="d-flex align-items-center gap-1">
                    <span className="text-muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>페이지당</span>
                    <select
                        className="form-select form-select-sm"
                        value={limit}
                        onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            setLimit(val);
                            localStorage.setItem(PAGE_SIZE_KEY, val);
                            setCurrentPage(1);
                            fetchTransfers(1, searchTerm, val);
                        }}
                        style={{ width: 80 }}
                    >
                        {[50, 100, 250, 500, 1000].map(n => (
                            <option key={n} value={n}>{n}개</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="table-responsive">
                {loading ? <div className="text-center p-4">로딩중...</div> : (
                    <table className="table table-bordered table-hover table-sm align-middle text-nowrap">
                        <thead className="table-light">
                            <tr>
                                <th className="text-center">번호</th>
                                <th className="text-center">사용자</th>
                                <th className="text-center">상호명 (입금자명)</th>
                                <th className="text-center">사업자번호 (이메일)</th>
                                <th className="text-center">결제금액</th>
                                <th className="text-center">상태</th>
                                <th className="text-center">적용 기간</th>
                                <th className="text-center">등록일시</th>
                                <th className="text-center">상세 정보</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transfers.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-4">데이터가 없습니다.</td></tr>}
                            {transfers.map((t, index) => {
                                let parsedInfo = {};
                                try {
                                    parsedInfo = typeof t.tax_invoice_info === 'string' ? JSON.parse(t.tax_invoice_info) : (t.tax_invoice_info || {});
                                } catch (e) {
                                    console.error("JSON parse error for tax_invoice_info", e);
                                }
                                return (
                                <tr key={t.id}>
                                    <td className="text-center">{(currentPage - 1) * limit + index + 1}</td>
                                    <td className="text-center">
                                        {t.user_name} <br/><small className="text-muted">({t.user_login_id})</small>
                                        <div className="mt-1">
                                            {parsedInfo?.proofType === 'tax_invoice' && <span className="badge bg-primary" style={{ fontSize: '10px' }}>세금계산서</span>}
                                            {parsedInfo?.proofType === 'cash_receipt' && <span className="badge bg-info text-white" style={{ fontSize: '10px' }}>현금영수증</span>}
                                            {(!parsedInfo?.proofType || parsedInfo?.proofType === 'none') && <span className="badge bg-secondary" style={{ fontSize: '10px' }}>증빙 안함</span>}
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <span className="fw-bold">{parsedInfo?.companyName || '-'}</span><br/>
                                        <small className="text-muted">{parsedInfo?.depositorName || '-'}</small>
                                    </td>
                                    <td className="text-center">
                                        {parsedInfo?.businessNumber || '-'}<br/>
                                        <small className="text-muted">{parsedInfo?.email || '-'}</small>
                                    </td>
                                    <td className="text-center text-end pe-3 fw-bold">
                                        {t.amount ? t.amount.toLocaleString() + '원' : '-'}
                                    </td>
                                    <td className="text-center">
                                        {t.status === 'WIRE_TRANSFER_PENDING' ? (
                                            <span className="badge bg-warning text-dark">승인대기</span>
                                        ) : t.status === 'DONE' ? (
                                            <span className="badge bg-success">승인완료</span>
                                        ) : t.status === 'REJECTED' ? (
                                            <span className="badge bg-danger">거절/취소</span>
                                        ) : (
                                            <span className="badge bg-secondary">{t.status}</span>
                                        )}
                                    </td>
                                    <td className="text-center" style={{ fontSize: '13px' }}>
                                        {parsedInfo?.approvedStartDate ? (
                                            <>
                                                {parsedInfo.approvedStartDate.split('T')[0]} <br/> ~ {parsedInfo.approvedEndDate?.split('T')[0]}
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td className="text-center">
                                        {new Date(t.created_at).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                    <td className="text-center">
                                        <button className="btn btn-sm btn-outline-primary" onClick={() => { setSelectedTransfer(t); setShowDetailModal(true); }}>
                                            정보 보기
                                        </button>
                                    </td>
                                </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
            {totalPages > 0 && renderPagination()}

            {showDetailModal && selectedTransfer && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} onClick={() => setShowDetailModal(false)}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-content" style={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                            {/* 헤더 */}
                            <div className="modal-header border-0 pb-0 px-4 pt-4">
                                <div>
                                    <h6 className="fw-bold mb-0" style={{ fontSize: 15, color: '#1a1a2e' }}>무통장 입금 신청 정보</h6>
                                    <span style={{ fontSize: 12, color: '#6c757d' }}>
                                        {selectedTransfer.tax_invoice_info?.proofType === 'cash_receipt' ? '현금영수증' : '세금계산서'} 발행 요청
                                    </span>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setShowDetailModal(false)}></button>
                            </div>

                            <div className="modal-body px-4 py-3">
                                {/* 고객 정보 */}
                                {selectedTransfer.tax_invoice_info ? (
                                    <div style={{ fontSize: 13, borderRadius: 8, border: '1px solid #f0f0f0', overflow: 'hidden', marginBottom: 12 }}>
                                        {[
                                            { label: '입금자명', value: selectedTransfer.tax_invoice_info.depositorName },
                                            ...(!selectedTransfer.tax_invoice_info.proofType || selectedTransfer.tax_invoice_info.proofType === 'tax_invoice' ? [
                                                { label: '상호명', value: selectedTransfer.tax_invoice_info.companyName },
                                                { label: '사업자번호', value: selectedTransfer.tax_invoice_info.businessNumber },
                                                { label: '대표자명', value: selectedTransfer.tax_invoice_info.ceoName },
                                                { label: '업태', value: selectedTransfer.tax_invoice_info.businessCondition || '-' },
                                                { label: '종목', value: selectedTransfer.tax_invoice_info.businessItem || '-' },
                                            ] : []),
                                            ...(selectedTransfer.tax_invoice_info.proofType === 'cash_receipt' ? [
                                                { label: '휴대폰 번호', value: selectedTransfer.tax_invoice_info.cashReceiptPhone },
                                            ] : []),
                                            { label: '이메일', value: selectedTransfer.tax_invoice_info.email },
                                        ].map(({ label, value }, i, arr) => (
                                            <div key={label} className="d-flex" style={{ borderBottom: i < arr.length - 1 ? '1px solid #f5f5f5' : 'none', padding: '8px 14px' }}>
                                                <span style={{ width: 90, color: '#8c8c8c', flexShrink: 0 }}>{label}</span>
                                                <span style={{ color: '#262626', fontWeight: 500 }}>{value}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : <p className="text-center text-muted my-3" style={{ fontSize: 13 }}>입력된 정보가 없습니다.</p>}

                                {/* 사업자등록증 */}
                                {(!selectedTransfer.tax_invoice_info?.proofType || selectedTransfer.tax_invoice_info?.proofType === 'tax_invoice') && selectedTransfer.business_license_url && (
                                    <a
                                        href={`${process.env.REACT_APP_API_URL.replace('/admin', '')}${selectedTransfer.business_license_url}`}
                                        target="_blank" rel="noreferrer"
                                        className="d-flex align-items-center gap-2 mb-3"
                                        style={{ textDecoration: 'none', color: '#1677ff', fontSize: 13, padding: '8px 12px', borderRadius: 8, border: '1px solid #e6f4ff', background: '#f0f7ff' }}
                                    >
                                        <i className="fas fa-file-alt"></i>
                                        <span>사업자등록증 사본 보기</span>
                                        <i className="fas fa-external-link-alt ms-auto" style={{ fontSize: 11 }}></i>
                                    </a>
                                )}

                                {/* 승인 설정 */}
                                {(selectedTransfer.status === 'WIRE_TRANSFER_PENDING' || selectedTransfer.status === 'DONE') && (
                                    <div style={{ borderRadius: 8, border: '1px solid #e8e8e8', padding: '14px 16px', background: '#fafafa' }}>
                                        {/* 등급 + 기간 헤더 */}
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <span style={{ fontSize: 13, fontWeight: 700, color: '#262626' }}>
                                                승인 설정
                                                {approveStartDate && approveEndDate && (() => {
                                                    const diff = Math.floor((new Date(approveEndDate) - new Date(approveStartDate)) / 86400000);
                                                    return <span className="badge bg-primary ms-2" style={{ fontSize: 11 }}>총 {diff}일</span>;
                                                })()}
                                            </span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto', fontSize: 12, minWidth: 160 }}
                                                value={approveGrade}
                                                onChange={e => {
                                                    setApproveGrade(e.target.value);
                                                    setApproveAmount(getVatAmount(e.target.value));
                                                }}
                                            >
                                                <option value="basic">Basic ({(gradePrices.basic || 0).toLocaleString()}원, VAT 별도)</option>
                                                <option value="premium">Premium ({(gradePrices.premium || 0).toLocaleString()}원, VAT 별도)</option>
                                            </select>
                                        </div>

                                        {/* 날짜 */}
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <input type="date" className="form-control form-control-sm" style={{ fontSize: 12 }}
                                                value={approveStartDate}
                                                onChange={e => {
                                                    setApproveStartDate(e.target.value);
                                                    if (approveEndDate && e.target.value > approveEndDate) setApproveEndDate(e.target.value);
                                                }} />
                                            <span style={{ color: '#8c8c8c', fontSize: 13 }}>~</span>
                                            <input type="date" className="form-control form-control-sm" style={{ fontSize: 12 }}
                                                min={approveStartDate}
                                                value={approveEndDate}
                                                onChange={e => setApproveEndDate(e.target.value)} />
                                        </div>
                                        <div className="d-flex gap-1 mb-3">
                                            {[30, 60, 90].map(d => (
                                                <button key={d} type="button" className="btn btn-outline-secondary btn-sm flex-fill" style={{ fontSize: 11, borderRadius: 6 }} onClick={() => handleSetDays(d)}>+{d}일</button>
                                            ))}
                                        </div>

                                        {/* 입금 금액 */}
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <label style={{ fontSize: 12, color: '#8c8c8c' }}>실제 입금 금액</label>
                                                <span style={{ fontSize: 12, color: '#1677ff', fontWeight: 600 }}>
                                                    고객 신청: {Number(selectedTransfer.amount).toLocaleString()}원
                                                </span>
                                            </div>
                                            <div className="input-group input-group-sm">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ fontSize: 13 }}
                                                    value={approveAmount ? Number(approveAmount).toLocaleString() : ''}
                                                    onChange={e => {
                                                        const raw = e.target.value.replace(/,/g, '');
                                                        if (/^\d*$/.test(raw)) setApproveAmount(raw);
                                                    }}
                                                />
                                                <span className="input-group-text">원</span>
                                            </div>
                                        </div>

                                        {/* 메모 */}
                                        <div>
                                            <label style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4, display: 'block' }}>관리자 메모 (선택)</label>
                                            <textarea className="form-control form-control-sm" rows="2"
                                                style={{ fontSize: 12, resize: 'none' }}
                                                placeholder="승인 또는 수정 사유 등"
                                                value={approveMemo}
                                                onChange={e => setApproveMemo(e.target.value)} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 푸터 */}
                            <div className="modal-footer border-0 px-4 pb-4 pt-2 d-flex justify-content-between">
                                <div className="d-flex gap-2">
                                    {selectedTransfer.status === 'WIRE_TRANSFER_PENDING' && (
                                        <>
                                            <button type="button" className="btn btn-primary btn-sm px-4 fw-bold" onClick={() => handleApprove(selectedTransfer.id)}>
                                                <i className="fas fa-check me-1"></i> 승인 처리
                                            </button>
                                            <button type="button" className="btn btn-danger btn-sm px-4 fw-bold" onClick={() => handleReject(selectedTransfer.id)}>
                                                <i className="fas fa-times me-1"></i> 거절 처리
                                            </button>
                                        </>
                                    )}
                                    {selectedTransfer.status === 'DONE' && (
                                        <button type="button" className="btn btn-primary btn-sm px-4 fw-bold" onClick={() => handleUpdateDates(selectedTransfer.id)}>
                                            <i className="fas fa-edit me-1"></i> 기간 수정
                                        </button>
                                    )}
                                </div>
                                <button type="button" className="btn btn-secondary btn-sm px-4" onClick={() => setShowDetailModal(false)}>닫기</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default WireTransferList;
